import { useEffect, useState } from 'react';
import css from './style.module.css';
import axios from 'axios';
import AddNewClient from './AddNewClient';
import EditClientInformation from './EditClientInformation';
import { useOktaConfig } from '../../Context/CredentialsContext';
import { useNavigate } from 'react-router-dom';
import LoadingCompanies from '../LoadingPages/LoadingCompanies';
import NoCompanies from './NoCompanies';

export default function Companies() {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [hoveredCard, setHoveredCard] = useState(null);
    const [CompaniesData, setCompaniesData] = useState([]);
    const [addClient, setAddClient] = useState(false);
    const [editClient, setEditClient] = useState(null); // Changed from false to null
    const [isLoading, setIsLoading] = useState(true);
    const { loginToken } = useOktaConfig();
    const navigation = useNavigate();

    useEffect(() => {
        const controller = new AbortController();

        const fetchData = async () => {
            if (!loginToken) return;
            setIsLoading(true);
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/get-all-companies`,
                    {
                        headers: { Authorization: `Bearer ${loginToken}` },
                        signal: controller.signal,
                    }
                );
                setCompaniesData(response.data.companies || []);
            } catch (error) {
                if (error?.code === "ERR_CANCELED" || axios.isCancel?.(error)) return;
                if (error?.response?.status === 401) {
                    localStorage.removeItem("Firebase Auth");
                    localStorage.removeItem("okta-token-storage");
                    localStorage.removeItem("okta-cache-storage");
                    navigation("/");
                    return;
                }
                console.error("Failed to fetch companies:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
        return () => controller.abort();
    }, [loginToken, navigation]); // removed isLoading


    if (isLoading) return <LoadingCompanies />;

    if (CompaniesData.length === 0) {
        return <NoCompanies />
    }


    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (value) {
            const results = CompaniesData.filter(item =>
                item.companyName.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredData(results);
        } else {
            setFilteredData([]);
        }
    };

    // Function to handle edit button click
    const handleEditClick = (company) => {
        setEditClient(company); // Pass the entire company object
    };

    const displayData = searchTerm ? filteredData : CompaniesData;

    return (
        <section className={`${css.companySection}`}>
            {/* Header */}
            <div className={css.header}>
                <div className={css.headerContent}>
                    <i className={`fas fa-building ${css.headerIcon}`}></i>
                    <div>
                        <h3 className={css.title}>Companies Configurations</h3>
                        <p className={css.subtitle}>
                            Manage clients and their configurations without exposing sensitive data
                        </p>
                    </div>
                </div>
            </div>

            {/* Search and Action Bar */}
            <div className={css.actionBar}>
                <div className={css.searchContainer}>
                    <input
                        type="text"
                        placeholder="Search by company name or email..."
                        value={searchTerm}
                        onChange={handleInputChange}
                        className={css.searchInput}
                    />
                </div>
                <button className={`${css.addButton} rounded-5`} onClick={() => setAddClient(true)}>
                    <i className="fas fa-plus"></i>
                    <span>Add New Client</span>
                </button>
            </div>

            {/* Results Info */}
            {searchTerm && (
                <div className={css.resultsInfo}>
                    Found <strong>{displayData.length}</strong> {displayData.length === 1 ? 'company' : 'companies'}
                </div>
            )}

            {/* Company Cards */}
            <div className="container mt-3">
                <div className="row">
                    {displayData.map((company) => (
                        <div key={company.id} className="col-md-4 mb-4">
                            <div
                                className={`${css.card} ${hoveredCard === company.id ? css.cardHover : ''}`}
                                onMouseEnter={() => setHoveredCard(company.id)}
                                onMouseLeave={() => setHoveredCard(null)}
                            >
                                {/* Status Badge */}
                                <div className={css.statusBadge}>
                                    <span className={`${css.statusText} ${company.status === true ? css.statusActive : css.statusInactive}`}>
                                        {company.status === true ? 'Active' : 'Inactive'}
                                    </span>
                                </div>

                                {/* Company Logo */}
                                <div className={css.logoContainer}>
                                    <img
                                        className={css.logo}
                                        src={company.logo}
                                        alt={`${company.companyName} Logo`}
                                    />
                                </div>

                                {/* Company Info */}
                                <div className={css.companyInfo}>
                                    <h4 className={css.companyName}>{company.companyName}</h4>
                                    <div className={css.emailContainer}>
                                        <i className={`fas fa-envelope ${css.emailIcon}`}></i>
                                        <p className={css.email}>{company.senderEmail}</p>
                                    </div>

                                    {/* Projects Badge */}
                                    <div className={css.projectsBadge}>
                                        <a className={css.projectsCount} href={company.companyDomain} target="_blank" rel="noreferrer" >{company.companyDomain}</a>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className={css.controllerButtons}>
                                    <button
                                        onClick={() => handleEditClick(company)}
                                        className={css.customCardButtonCompanies}
                                    >
                                        <i className="fas fa-edit"></i>
                                        <span>Edit</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Add New Client */}
                {addClient && <AddNewClient loginToken={loginToken} addClient={addClient} setAddClient={setAddClient} setCompaniesData={setCompaniesData} />}

                {/* Edit Client - Pass the company data */}
                {editClient && <EditClientInformation loginToken={loginToken} editClient={editClient} setEditClient={setEditClient} setCompaniesData={setCompaniesData} />}

                {/* Empty State */}
                {displayData.length === 0 && (
                    <NoCompanies />
                )}
            </div>
        </section>
    );
}